import http from '@/http'
import { AxiosResponse } from 'axios'
import {
  GetTagNamesParams,
  TagNameListResponse,
  TagNameAdminDto
} from '@/interfaces/admin/tagName'

export const deleteTag = async (tagId: number): Promise<void> => {
  await http.delete(`/admin/tag-name/${tagId}`)
}

export const getUsedCountForTag = async (tagId: number): Promise<number> => {
  const params = {
    limit: 1001
  }
  return http.get(`/admin/tag-name/${tagId}/used`, { params })
    .then(res => res.data)
}

export const loadTag = async (tagId: number): Promise<AxiosResponse> => {
  return http.get(`/admin/tag-name/${tagId}`)
}

export const searchTags = async (params: any): Promise<AxiosResponse> => {
  return http.get('/admin/tag-name', { params })
}

/**
 * Search tag names with type-safe parameters and response.
 * 
 * This function provides a comprehensive API for retrieving tag name metadata
 * with flexible filtering options, including the ability to exclude range-type tags.
 * 
 * @param params - Query parameters for filtering, sorting, and pagination
 * @returns Promise resolving to tag name list with pagination metadata
 * 
 * @example
 * // Get all part tags excluding range tags
 * const response = await searchTagNames({
 *   applicableType: 'PART',
 *   excludeRangeTags: true,
 *   sort: 'displayOrder'
 * });
 * 
 * @example
 * // Search for tags by name with usage counts
 * const response = await searchTagNames({
 *   filter: 'color',
 *   includeUsedCount: true,
 *   limit: 20
 * });
 * 
 * @example
 * // Get all content tags (PART, MEDIA, CHAPTER, PAGE, PAGE_PART, PRODUCT_PAGE)
 * const response = await searchTagNames({
 *   applicableType: 'CONTENT',
 *   limit: 50
 * });
 */
export const searchTagNames = async (
  params: GetTagNamesParams = {}
): Promise<TagNameListResponse> => {
  const response = await http.get<TagNameAdminDto[]>('/admin/tag-name', { params })
  
  // Extract pagination metadata from response headers
  const pagination = {
    limit: parseInt(response.headers['x-pagination-limit'] || '100'),
    offset: parseInt(response.headers['x-pagination-offset'] || '0'),
    count: parseInt(response.headers['x-pagination-count'] || '0'),
    total: parseInt(response.headers['x-pagination-total'] || '0')
  }
  
  return {
    data: response.data,
    pagination
  }
}

export const updateTag = async (tagId: number, dto: any): Promise<AxiosResponse> => {
  return http.put(`/admin/tag-name/${tagId}`, dto)
}
